import type {
	InputEntryResponse,
	InputEntryData,
} from "../../../pages/activityBuilder/modules/inputs/inputsTypes";
import { convertEmptyStringsToNull } from "../../../pages/activityBuilder/modules/inputs/utils";
import { EntryCreatePayload, EntryDeletePayload, EntryUpdatePayload, ParentPayload, ParentResponse, ParentUpdatePayload } from "../../../types/modulesInterfaces";
import { FEModules } from "../../../utils/moduleList";
import { FEModulesObj } from "../../../utils/moduleList";
import { apiSlice, publicApiSlice, TagTypes } from "../../apiSlice";
import { createEntriesGetApi, createModuleGetApi } from "./factories/createModuleApi";

// Define constants for the module
const MODULE_TYPE = "Inputs";
const TAG: TagTypes = "Inputs";
const ENDPOINT = FEModulesObj[FEModules.Inputs].path;

const ENTRY_MODULE_TYPE = "InputEntries";
const TAG_ENTRY: TagTypes = "InputEntries";
export const TAG_ENTRY_DEFAULTS: TagTypes = "InputEntriesDefaults";
export const ENTRY_ENDPOINT = "input-entries";

const inputsApiSlice = createModuleGetApi<ParentResponse>({
	apiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
});

const inputsEntriesApiSlice = createEntriesGetApi<InputEntryData[]>({
	apiSlice,
	path: ENTRY_ENDPOINT,
	moduleName: ENTRY_MODULE_TYPE,
	tag: TAG_ENTRY,
	tagDefaults: TAG_ENTRY_DEFAULTS,
	transformResponse: (response: InputEntryData[]) => response.sort((a, b) => a.id - b.id),
});

const publicInputsApiSlice = createModuleGetApi<ParentResponse>({
	apiSlice: publicApiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
});

const publicInputsEntriesApiSlice = createEntriesGetApi<InputEntryData[]>({
	apiSlice: publicApiSlice,
	path: ENTRY_ENDPOINT,
	moduleName: ENTRY_MODULE_TYPE,
	tag: TAG_ENTRY,
	tagDefaults: TAG_ENTRY_DEFAULTS,
	transformResponse: (response: InputEntryData[]) => response.sort((a, b) => a.id - b.id),
});

export const { useGetInputsQuery } = inputsApiSlice;
export const { useGetInputEntriesQuery, useGetInputEntriesDefaultsQuery } = inputsEntriesApiSlice;

export const { useGetInputsQuery: usePublicGetInputsQuery } = publicInputsApiSlice;
export const { 
	useGetInputEntriesQuery: usePublicGetInputEntriesQuery, 
	useGetInputEntriesDefaultsQuery: usePublicGetInputEntriesDefaultsQuery 
} = publicInputsEntriesApiSlice;

export const mutationApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		updateInputs: builder.mutation<ParentResponse, ParentUpdatePayload<ParentPayload>>({
			query: ({ body }) => {
				const { id, ...rest } = body
				convertEmptyStringsToNull(rest)
				return { url: `inputs/${id}/`, method: 'PUT', body: rest }
			},
			invalidatesTags: (_results, _error, { activityId, body }) => [
				{ type: 'Inputs', id: activityId },
				{ type: "ModulesStatuses", id: activityId },
				{ type: "ModuleResults", id: body?.id ? `inputs-${body.id}` : undefined },
				{ type: "ModuleHistory", id: body?.id ? `inputs-${body.id}` : undefined }]
		}),
		updateInputEntry: builder.mutation<
			InputEntryResponse,
			EntryUpdatePayload<InputEntryResponse>
		>({
			query: ({ body }) => {
				const { id, ...rest } = body;
				convertEmptyStringsToNull(rest)
				return {
					url: `input-entries/${id}/`,
					method: "PUT",
					body: rest,
				};
			},
			invalidatesTags: (_results, _error, { activityId, body }) => [
				{ type: "InputEntries", id: activityId },
				{ type: "InputEntriesDefaults", id: body?.id ? body?.id : undefined },
			],
		}),
		createInputEntry: builder.mutation<
			InputEntryResponse,
			EntryCreatePayload
		>({
			query: ({ activityId, parent }) => ({ url: "input-entries/", method: 'POST', body: { activity: activityId, parent } }),
			invalidatesTags: (_results, _error, { activityId, parent }) => [
				{ type: "InputEntries", id: activityId },
				{ type: "ModuleResults", id: parent ? `inputs-${parent}` : undefined },
				{ type: "ModuleHistory", id: parent ? `inputs-${parent}` : undefined },
				{ type: "ModulesStatuses", id: activityId },
			],
		}),
		deleteInputEntry: builder.mutation<void, EntryDeletePayload>({
			query: ({ id }) => ({ url: `input-entries/${id}/`, method: 'DELETE' }),
			invalidatesTags: (_results, _error, { activityId, parent, id }) => [
				{ type: "InputEntries", id: activityId },
				{ type: "ModuleResults", id: parent ? `inputs-${parent}` : undefined },
				{ type: "ModuleHistory", id: parent ? `inputs-${parent}` : undefined },
				{ type: "ModulesStatuses", id: activityId },
			],
		}),
	}),
});

export const {
	useUpdateInputsMutation,
	useCreateInputEntryMutation,
	useUpdateInputEntryMutation,
	useDeleteInputEntryMutation,
} = mutationApiSlice;
